import { LucideIcon, Check } from 'lucide-react'
import EpisodeCardWithIcon from './EpisodeCardWithIcon'
import { motion } from 'framer-motion'

interface Episode {
  id: number
  number: number
  title: string
  icon: LucideIcon
  thumbnailUrl?: string | null
  status?: string
}

export interface EpisodeProgressMap {
  [episodeId: number]: { completed: boolean; progressPercent: number }
}

interface FeaturedModuleSectionProps {
  moduleId: number
  moduleOrder: number
  title: string
  description: string
  episodes: Episode[]
  courseTitle?: string
  isLatestCourse?: boolean
  progressMap?: EpisodeProgressMap
}

const FeaturedModuleSection = ({
  moduleId,
  moduleOrder,
  title,
  description,
  episodes,
  courseTitle,
  isLatestCourse = false,
  progressMap,
}: FeaturedModuleSectionProps) => {
  const allEpisodesCompleted =
    episodes.length > 0 && episodes.every((ep) => progressMap?.[ep.id]?.completed)

  return (
    <section id={`module-${moduleOrder}`} className="py-16 bg-background">
      <div className="container">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Module Info - Left Column */}
          <motion.div
            className="lg:col-span-4 space-y-4"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge de status do módulo */}
            {isLatestCourse && allEpisodesCompleted && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 text-green-500 text-xs font-semibold rounded-full mb-2 border border-green-500/20">
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
                Finalizado
              </div>
            )}
            {isLatestCourse && !allEpisodesCompleted && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary text-xs font-semibold rounded-full mb-2 border border-primary/20">
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                Em Andamento
              </div>
            )}

            <span className="module-badge">Módulo {moduleOrder}</span>

            <h3 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
              {title}
            </h3>

            <p className="text-muted-foreground leading-relaxed">
              {description.replace(/<[^>]*>/g, '')}
            </p>

            {/* Título do curso (opcional) */}
            {courseTitle && isLatestCourse && (
              <div className="pt-2 mt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground/70">
                  Curso: <span className="font-medium text-muted-foreground">{courseTitle}</span>
                </p>
              </div>
            )}
          </motion.div>

          {/* Episodes Grid - Right Column */}
          <motion.div
            className="lg:col-span-8"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {episodes.map((episode, index) => (
                <motion.div
                  key={episode.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <EpisodeCardWithIcon
                    moduleId={moduleId}
                    moduleOrder={moduleOrder}
                    episodeNumber={episode.number}
                    title={episode.title}
                    icon={episode.icon}
                    thumbnailUrl={episode.thumbnailUrl}
                    completed={progressMap?.[episode.id]?.completed}
                    progressPercent={progressMap?.[episode.id]?.progressPercent}
                    status={episode.status}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default FeaturedModuleSection
